import {grey, orange, red} from "@ant-design/colors";
import {Button, Space, Tag, Tooltip} from "antd";
import moment from "moment";
import React, {FC, memo} from "react";
import {TableType} from "typeorm/metadata/types/TableTypes";
import {DeleteFilled, ReloadOutlined} from "@ant-design/icons";
import {ArrayDataOptions, Maybe} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import {marginS, marginXs} from "../../../styles/layout";
import {mailjetStatusOptions} from "../../notifications/emaiNotificationStatusText";
import {NDN_FILTER_ABSENT, NDN_FILTER_PRESENT} from "../../../../shared/const/nonDisclosureNoticeFilter";
import {naturalLanguageAge} from "../../../helpers/natualLanguageAge";
import {sanitizeTagValues} from "../../../helpers/tableHelpers/FilterDropdownSearchMultiple";
import {BadgeState, badgeStateLabel} from "../../../views/PaymentMethodBadge/deriveBadgeState";
import {mollieMethodLabel} from "../../../helpers/mollieMethodLabels";
// import {mailjetStatusOptions} from "../../notifications/SentMailsListColumns";

// Spalten, die ueber ein Tag-Input mehrere Werte filtern (type "searchMultiple").
// Hier eintragen, um die Mehrwert-Anzeige in den aktiven Filtern zu aktivieren.
const MULTI_VALUE_FILTER_COLUMNS: Array<string> = ["prefixedCourseNumber", "courseNumber"];

type ActiveFiltersProps = {
    options?: Maybe<ArrayDataOptions>;
    setOptions: (options: ArrayDataOptions) => void;
    refetch: () => void;
    hiddenFilters?: Array<string>;
    protectedFilters?: Array<string>;
    style?: React.CSSProperties;
};

const ActiveFilters: FC<ActiveFiltersProps> = ({options, setOptions, refetch, hiddenFilters, protectedFilters, style}) => {
    const resetFilters = (column: keyof TableType) => {
        const resetedOptions = {...options};

        const updatedFilters = options?.filter?.filter((el) => {
            return el.column !== column;
        });

        resetedOptions.filter = updatedFilters;
        // resetedOptions.filter = [];
        console.log("resetedOptions::::", resetedOptions);

        setOptions(resetedOptions);
        // refetch();
        // window.location.reload(); // THIS IS ONLY TO ENSURE THE PREVIOUS REMOVED FILTERS ARE GONE

        console.log("updatedFilters::::", updatedFilters);
    };

    const generalStyle: React.CSSProperties = {
        // position: "absolute",
        right: marginS,
        marginTop: `calc((${marginXs} / 2 * -1) + 1px)`,
        marginLeft: "auto",
    };

    const ResetFilters = ({icon}: {icon: "delete" | " refetch"}) => {
        // Behalte protectedFilters beim Zurücksetzen
        const protectedFiltersToKeep = options?.filter?.filter((filter) => protectedFilters?.includes(filter.column)) ?? [];

        const defaultOptions: ArrayDataOptions = {
            filter: protectedFiltersToKeep,
            limit: 15,
            offset: 0,
            sortColumn: undefined,
            sortOrder: undefined,
        };

        const tooltipTitle = icon === "delete" ? "Alle Filter zurücksetzen" : "Liste neu laden";

        return (
            <Button
                size="small"
                type="text"
                // style={{fontSize: "0.9em", padding: 0, marginLeft: marginS}}
                style={{paddingRight: 0, paddingLeft: 0, marginRight: 0, paddingTop: 2}}
                onClick={() => {
                    setOptions(defaultOptions);
                    refetch();
                }}
            >
                <Tooltip title={tooltipTitle} placement="left">
                    {icon === "delete" ? (
                        <DeleteFilled style={{color: red[3]}} />
                    ) : (
                        <ReloadOutlined
                            style={{
                                color: orange[5],
                                fontSize: "0.9em",
                                marginLeft: 5,
                            }}
                        />
                    )}
                </Tooltip>
            </Button>
        );
    };

    if (options?.filter?.length) {
        return (
            <div style={{minHeight: marginS, display: "flex", gap: 2}}>
                <div style={{fontSize: "0.9em", whiteSpace: "nowrap", alignSelf: "center", /* marginTop: -5, */ alignItems: "center"}}>{"Aktive Filter: "}</div>
                <Space style={{...generalStyle, flexWrap: "wrap", alignItems: "center"}} size={3}>
                    {options.filter.map((filter) => {
                        // Ausblenden wenn in hiddenFilters oder protectedFilters
                        if (hiddenFilters?.includes(filter.column as keyof string) || protectedFilters?.includes(filter.column)) {
                            return null;
                        }

                        const col: keyof TableType = filter.column as keyof TableType;

                        const dateEntryArray = ["firstCourseLesson", "lastCourseLesson", "courseLesson", "dateOfBooking", "paymentDue", "paymentDone", "payDate", "invoiceDate", "birthDate", "birthday", "updateDate", "createDate"];

                        const timeEntryArray = ["startTimeOfLesson", "endTimeOfLesson"];

                        const numberRangeEntryArray = [
                            "credit",
                            "lessonCount",
                            "bookingsCount",
                            "placeReservationsCount",
                            "waitlistCount",
                            "grossPrice",
                            "netPrice",
                            "vat",
                            "coursePrice",
                            "durationInMinutes",
                            "costForLesson",
                            "rate",
                            "indexOfLessonInCourse",
                            "fee",
                            "minAttendees",
                            "maxAttendees",
                            "amount",
                        ];

                        // console.log("col:::", col);

                        const valueToshow = () => {
                            const dateIsNotSet = (date: Date | undefined | string) => {
                                return date === undefined || date === null || date.toString() === "Invalid Date" || date === "";
                            };

                            // DATE FILTERS ---------------------------------------------------------
                            if (dateEntryArray.includes(col as string)) {
                                if (dateIsNotSet(filter.values[0]) && dateIsNotSet(filter.values[1])) {
                                    return "Kein Datum gesetzt";
                                } else if (!dateIsNotSet(filter.values[0]) && !dateIsNotSet(filter.values[1])) {
                                    const startDate = moment(filter.values[0]);
                                    const endDate = moment(filter.values[1]);

                                    // Check if this is a full month range (e.g., from courseLesson filter)
                                    const isFullMonth =
                                        col === ("courseLesson" as string) &&
                                        startDate.isSame(startDate.clone().startOf("month"), "day") &&
                                        endDate.isSame(endDate.clone().endOf("month"), "day") &&
                                        startDate.isSame(endDate, "month");

                                    if (isFullMonth) {
                                        return startDate.format("MMMM YYYY");
                                    }

                                    return `${startDate.format("DD.MM.YY")} - ${endDate.format("DD.MM.YY")}`;
                                } else if (dateIsNotSet(filter.values[0])) {
                                    return `Bis einschließlich ${moment(filter.values[1]).format("DD.MM.YY")}`;
                                }

                                return `Ab einschließlich ${moment(filter.values[0]).format("DD.MM.YY")}`;
                            }

                            // TIME FILTERS ---------------------------------------------------------
                            if (timeEntryArray.includes(col as string)) {
                                if (dateIsNotSet(filter.values[0]) && dateIsNotSet(filter.values[1])) {
                                    return "Keine Uhrzeit gesetzt";
                                } else if (!dateIsNotSet(filter.values[0]) && !dateIsNotSet(filter.values[1])) {
                                    return `${moment(filter.values[0]).format("HH:mm")} - ${moment(filter.values[1]).format("HH:mm")}`;
                                } else if (dateIsNotSet(filter.values[0])) {
                                    return `Bis ${moment(filter.values[1]).format("HH:mm")} Uhr`;
                                }

                                return `Ab ${moment(filter.values[0]).format("HH:mm")} Uhr`;
                            }

                            // AGE FILTER -----------------------------------------------------------
                            // Werte sind in Gesamtmonaten gespeichert (vgl. FilterDropdownAge)
                            if (col === ("age" as string)) {
                                const AGE_MAX_MONTHS = 1200;
                                const minStr = filter.values[0];
                                const maxStr = filter.values[1];
                                const minMonths = Number(minStr);
                                const maxMonths = Number(maxStr);

                                // Formatierung: "0 M" wenn 0 Monate, sonst naturalLanguageAge()
                                const formatAge = (totalMonths: number): string => {
                                    if (!Number.isFinite(totalMonths) || totalMonths <= 0) {
                                        return `0 ${i18n.containers.courses.CourseTypes.m()}`;
                                    }

                                    return naturalLanguageAge(totalMonths).trim();
                                };

                                // Modus erkennen
                                if (minStr === maxStr) {
                                    return `= ${formatAge(minMonths)}`;
                                } else if (minStr !== "0" && maxStr === String(AGE_MAX_MONTHS)) {
                                    return `≥ ${formatAge(minMonths)}`;
                                } else if (minStr === "0" && maxStr !== String(AGE_MAX_MONTHS)) {
                                    return `≤ ${formatAge(maxMonths)}`;
                                } else if (minStr === "0" && maxStr === String(AGE_MAX_MONTHS)) {
                                    return "Alle";
                                }

                                return `${formatAge(minMonths)} – ${formatAge(maxMonths)}`;
                            }

                            if (numberRangeEntryArray.includes(col as string)) {
                                const minValue = filter.values[0];
                                const maxValue = filter.values[1];

                                // Spezial-Fall für credit
                                if (col === ("credit" as string)) {
                                    if (minValue === "0" && maxValue === "0") {
                                        return "Ohne Gutschrift";
                                    } else if (minValue === "1" && maxValue === "10000") {
                                        return "Mit Gutschrift";
                                    }
                                }

                                // Erkennung des Filter-Modus
                                if (minValue === maxValue) {
                                    // Genau dieser Wert (=)
                                    return `= ${minValue}`;
                                } else if (minValue !== "0" && maxValue === "999999") {
                                    // Größer gleich (>=)
                                    return `≥ ${minValue}`;
                                } else if (minValue === "0" && maxValue !== "999999") {
                                    // Kleiner gleich (<=)
                                    return `≤ ${maxValue}`;
                                } else if (minValue === "0" && maxValue === "999999") {
                                    // Alle Werte (kein echter Filter)
                                    return "Alle";
                                } else {
                                    // Bereich von-bis
                                    return `${minValue} - ${maxValue}`;
                                }
                            }

                            if (col === ("status" as string)) {
                                console.log("col is status");
                                // translate mailjet status
                                const status = mailjetStatusOptions.find((el) => el.value === filter.values[0]);

                                if (status) {
                                    return status.label;
                                }

                                // Mollie-Zahlungen: Lifecycle-Zustand des Badges → Label.
                                const badgeStates = Object.values(BadgeState) as Array<string>;
                                if (badgeStates.includes(filter.values[0])) {
                                    return badgeStateLabel(filter.values[0] as BadgeState);
                                }

                                // Interessenten-Bekundungen: Status-Enum → Deutsch (Multi-Select).
                                const interestStatus: Record<string, string> = {New: "Neu", Contacted: "Kontaktiert", Done: "Erledigt"};
                                const interestLabels = filter.values.map((v) => interestStatus[v]).filter(Boolean);
                                if (interestLabels.length > 0) {
                                    return interestLabels.join(", ");
                                }
                            }

                            // Mollie-Zahlungen: Zahlungsart (creditcard, paypal, …) → Label.
                            if (col === ("method" as string)) {
                                return mollieMethodLabel(filter.values[0]);
                            }

                            if (col === ("paymentMethods" as string)) {
                                const paymentMethodLabels: Record<string, string> = {
                                    cash: "Bar",
                                    sepaDebit: "Lastschrift",
                                    bankTransfer: "Überweisung",
                                    esr: "Einzahlungsschein",
                                    onlinePayment: "Online-Zahlung",
                                };
                                return paymentMethodLabels[filter.values[0]] ?? filter.values[0];
                            }

                            // console.log(
                            //     "filter.values[0]:::",
                            //     filter.values[0],
                            // );

                            // sc-3040 follow-up — Sperrvermerk-Presence-Filter
                            if (col === ("nonDisclosureNotice" as string)) {
                                if (filter.values[0] === NDN_FILTER_PRESENT) return "vorhanden";
                                if (filter.values[0] === NDN_FILTER_ABSENT) return "nicht vorhanden";
                            }

                            // Bezahlturnus (Kursarten-Liste)
                            if (col === ("paymentInterval" as string)) {
                                if (filter.values[0] === "PerCourse") return i18n.containers.courses.CourseCreate.paymentInterval.perCourse();
                                if (filter.values[0] === "PerLesson") return i18n.containers.courses.CourseCreate.paymentInterval.perLesson();
                            }

                            // Schalter „Stornierte ausblenden“ → Tag „Stornierte: ausgeblendet“
                            if (col === ("hideCanceled" as string)) return "ausgeblendet";

                            if (filter.values[0] === "true") return "Ja";
                            if (filter.values[0] === "false") return "Nein";

                            // hidden values
                            if (["bookingOnLocation"].includes(col as string)) {
                                return "";
                            }

                            // MULTI-VALUE FILTERS (Tag-Input, type "searchMultiple")
                            // Leer- und Legacy-Timestamp-Token herausfiltern und
                            // mehrere Werte komma-separiert anzeigen.
                            if (MULTI_VALUE_FILTER_COLUMNS.includes(col as string)) {
                                const cleanValues = sanitizeTagValues(filter.values);

                                if (cleanValues.length > 1) {
                                    return cleanValues.join(", ");
                                }

                                if (cleanValues.length === 1) {
                                    return cleanValues[0];
                                }

                                // Nur Leer-/Timestamp-Token uebrig -> nichts anzeigen
                                // (nicht auf filter.values[0] zurueckfallen, sonst
                                // erschiene ein roher Cache-Buster-Timestamp).
                                return "";
                            }

                            return filter.values[0];
                        };

                        // Determine column label - use "Zeitraum" for courseLesson filter
                        const getColumnLabel = () => {
                            if (col === ("courseLesson" as string)) {
                                return "Zeitraum";
                            }

                            return i18n.views.TableColumnTitles[col]();
                        };

                        return (
                            <Tag
                                key={filter.column}
                                closable
                                onClose={() => resetFilters(col)}
                                color={orange[5]}
                                style={{
                                    // padding: "-5px 10px",
                                    padding: "0px 10px",
                                    lineHeight: "1.4",
                                    // margin: 0,
                                    // marginBottom: 2,
                                    // border: "1px solid blue",
                                }}
                            >
                                {`${getColumnLabel()}: ${
                                    // filter.values[0]
                                    valueToshow()
                                }`}
                            </Tag>
                        );
                    })}
                    <ResetFilters icon="delete" />
                </Space>
            </div>
        );
    }

    return (
        <div style={{...generalStyle, ...style, color: grey[2], fontSize: "0.9em", display: "flex", justifyContent: "flex-end"}}>
            <div style={{display: "flex", flex: 1, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center"}}>
                {"Keine aktiven Filter"}
                <ResetFilters icon=" refetch" />
            </div>
        </div>
    );
};

export default memo(ActiveFilters);
